import { ArrowRight, Check } from "lucide-react";
import type { AvailableApp } from "@/lib/api/controlPlaneClient";
import { cn } from "@/lib/utils";
import { appBannerColor } from "./appCardColors";
import { AppLogo } from "./AppLogo";

/** Canva-style discover card dimensions (fixed so every shelf/grid card matches). */
export const CANVA_CARD_WIDTH = 232;

/** Per-app product screenshot shown peeking from the card banner (keyed by app name). */
const CARD_BANNER_IMAGES: Record<string, string> = {
  crm: "/card-crm.jpg",
};

function subtitleFor(app: AvailableApp): string {
  if (app.category) return app.category;
  if (app.pricing_type === "Paid") return `From ฿${app.monthly_price_thb ?? 0}/mo`;
  if (app.pricing_type === "Freemium") return "Free plan available";
  return "Free to install";
}

/**
 * Canva-style discover card: a colourful banner carrying the app's tagline and
 * logo, with a footer holding the name and a short descriptor. Fixed width and
 * banner proportions match Canva's "Top picks" cards.
 */
export function CanvaAppCard({
  app,
  isInstalled,
  onClick,
}: {
  app: AvailableApp;
  isInstalled?: boolean;
  onClick?: (app: AvailableApp) => void;
}) {
  const banner = appBannerColor(app.name);
  const bannerImage = CARD_BANNER_IMAGES[app.name];

  return (
    <button
      type="button"
      onClick={() => onClick?.(app)}
      aria-label={`${app.app_name} — ${app.description}`}
      style={{ width: CANVA_CARD_WIDTH }}
      className={cn(
        "group relative flex flex-col overflow-hidden rounded-2xl text-left",
        "border border-gray-200/70 dark:border-gray-700/60 bg-white dark:bg-gray-900",
        "transition-all duration-200 ease-out hover:-translate-y-0.5 hover:shadow-apple-md",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2794F0] focus-visible:ring-offset-2"
      )}
    >
      {/* Banner — tagline + product image (or logo fallback), Canva 4:3-ish proportions */}
      <div
        className="relative flex h-[172px] flex-col overflow-hidden"
        style={{ backgroundColor: banner }}
      >
        <p className="line-clamp-2 px-4 pb-1 pr-6 pt-4 text-[13px] font-semibold leading-snug text-[#1d1f26]">
          {app.description}
        </p>
        <div className="relative mt-1 flex-1">
          {bannerImage ? (
            <img
              src={bannerImage}
              alt=""
              aria-hidden="true"
              className="absolute inset-x-4 bottom-0 top-0 h-full rounded-t-xl object-cover object-top shadow-md ring-1 ring-black/10"
            />
          ) : (
            <div className="flex h-full items-center justify-center pb-3">
              <AppLogo app={app} size={60} radius={15} />
            </div>
          )}
        </div>
        <span
          className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full bg-white/70 text-[#1d1f26] opacity-0 transition-opacity duration-200 group-hover:opacity-100"
          aria-hidden="true"
        >
          <ArrowRight className="h-4 w-4" strokeWidth={2} />
        </span>
      </div>

      {/* Footer — logo + name + descriptor */}
      <div className="flex items-center gap-2 px-4 py-3">
        <AppLogo app={app} size={28} radius={8} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1">
            <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
              {app.app_name}
            </p>
            {isInstalled && (
              <Check className="h-3.5 w-3.5 shrink-0 text-green-600" strokeWidth={3} />
            )}
          </div>
          <p className="truncate text-xs text-gray-500 dark:text-gray-400">
            {subtitleFor(app)}
          </p>
        </div>
      </div>
    </button>
  );
}
